import { memo, useState } from 'react'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { KOMA_DIVIDER_BORDER } from '~/models/komaDivider'
import { formatCellValue } from '~/pages/Supply48Data/models/cellValue'
import {
  COLOR_BORDER_LOW,
  COLOR_TEXT_HIGH,
  COLOR_TEXT_LOW,
  S48_DATA_COL_WIDTH_PX,
  cellBackground,
} from '~/pages/Supply48Data/models/gridStyles'

type Props = {
  /** aria-label 用: 日付ラベル (M/D 形式) */
  dateLabel: string
  /** aria-label 用: コマラベル (HH:MM 形式) */
  komaLabel: string
  /**
   * aria-label 用: 列の表示名 (`sourceDisplayLabel` の結果 — 取引 source は
   * 取引先名、市場 source は market_kind ラベル)。
   */
  columnLabel: string
  /** グリッド座標 (キーボード操作はこの data 属性から位置を解決する) */
  row: number
  col: number
  /**
   * 表示単位 (AC-S4D-037) に換算済みの値。保存値は kWh/30min 固定。
   * null は編集中に明示的にクリアしたセルのみを指す (Delete/Backspace)。
   * サーバー値が未設定のコマは `displaySlotValue` (gridRows.ts) が既に 0 へ
   * fallback しているため、この prop には現れない (AC-S4D-050 / AC-S4D-053 ※)。
   */
  displayValue: number | null
  /**
   * 編集不可の視覚表現 (グレーアウト・disabled) を出すか。GC ロック済みコマに
   * 加え、市場列 (AC-S4D-032: 常時 read-only) もこの表現を流用する。
   */
  locked: boolean
  /**
   * 「編集中（未保存の手動変更）」か (AC-S4D-049)。 `editState.isKomaEditing` の
   * 結果を渡す。 「変更後」(isSavedManual) とは独立したレイヤで、 両方同時に
   * true になりうる (既に保存済みの手動変更セルを、このセッションでさらに
   * 上書き編集中の場合)。
   */
  isEditing: boolean
  /** 「変更後（保存済みの手動変更）」か (AC-S4D-049)。 `editState.isKomaSavedManual` の結果を渡す。 */
  isSavedManual: boolean
  /**
   * 「エラー（保存がバリデーションで拒否された）」の tooltip 文言 (AC-S4D-049)。
   * undefined ならエラー状態ではない。 定義済みならエラー表現 (赤バー・outline・
   * 赤字) を出し、 native `title` にこの文言を載せる (design `data-tooltip` と
   * 同じ「器が overflow を持つため native tooltip を使う」理由、48data.html:225 参照)。
   */
  saveErrorTooltip: string | undefined
  /** 契約値超過か */
  isOverContract: boolean
  /**
   * AC-S4D-052: 相対取引契約管理からの遷移直後の一時列ハイライト中か。
   * design `.s48-chart-col` 準拠 (列の左右を 2px の accent 枠で囲む)。
   * 選択枠 (`showSelectionRing`、inset 全周枠) とは意図的に別の視覚語彙にする
   * (Des 判断、PR #7935 レビュー 2026-09-15: 選択枠を流用すると「この列が
   * 選択されている＝入力できる」と誤読される)。
   */
  isScrollHighlighted: boolean
  /**
   * グリッド全体が書き込み可能なモードか (= 閲覧モードでないか。hook 側の
   * `enabled` / `canEdit` と同じ概念、P5)。個別セルの GC ロックは `locked` が
   * 別に持つ (hook の `GridCellInfo.editable` = enabled && !locked と同名衝突
   * していたため、このセル単位判定と紛れないよう `enabled` に統一する)。
   */
  enabled: boolean
  /** 矩形選択の範囲内か (AC-S4D-003 のセル選択、複数セルのみ)。単一セルの
   * 選択枠は GridCell 側のローカル focus 状態で表現する (P3)。
   */
  isSelected: boolean
  /** 行の偶数/奇数インデックス (背景色の交互表示用) */
  /** 縞を敷く側の行か (行単位 zebra、 8080 準拠)。 */
  isZebraRow: boolean
  /**
   * 3 時間 (6 コマ) ごとの区切りの行か (conventions.md §48 コマの表の区切り線)。
   * 表は border-collapse: separate なので、 線は各セルの下線を濃くして引く。
   */
  isKomaDividerRow?: boolean
  /**
   * セル入力ハンドラ。identity が安定した 1 つの関数を全セルで共有する
   * (セル位置は input の data 属性から解決するため closure を作らない)。
   * keydown / copy / paste / focus はグリッドのコンテナ側で委譲処理する。
   */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * 48 点データグリッドの 1 データセル。
 *
 * memo 化により、毎キーストロークで全セル re-render される問題を緩和する。
 * props はすべてプリミティブ + 安定 handler にしてあり、値・選択状態・ロック
 * 状態が変わったセルだけが再 render される。
 */
export const GridCell = memo(function GridCell({
  dateLabel,
  komaLabel,
  columnLabel,
  row,
  col,
  displayValue,
  locked,
  isEditing,
  isSavedManual,
  saveErrorTooltip,
  isOverContract,
  isScrollHighlighted,
  enabled,
  isSelected,
  isZebraRow,
  isKomaDividerRow = false,
  onChange,
}: Props): React.ReactElement {
  /*
   * P3: 単一セルの選択枠はこのセルローカルの focus 状態で表現する
   * (grid 側の共有 state に 1×1 の range を持たせない)。矢印キー移動のたびに
   * grid 全体が再 render される問題 (最大 1 ヶ月表示 = 1488 行で顕著) と、
   * blur 後も選択枠が残る不具合を避ける。isSelected (複数セル範囲) と
   * isFocused (単一セル) はどちらも同じ視覚言語 (accent 2px 枠) を共有する。
   */
  const [isFocused, setIsFocused] = useState(false)
  const showSelectionRing = isSelected || isFocused
  const hasSaveError = saveErrorTooltip !== undefined

  const bgColor = cellBackground({
    locked,
    isEditing,
    isOverContract,
    isZebraRow,
  })

  /*
   * [#5125 / #8988 / AC-S4D-049] 編集中 (未保存の手動変更) の左 3px accent
   * バー、またはエラー (保存拒否) の左 3px negative バー。「同じ位置・違う色」
   * (design `48data.html:248-252` の `cell-save-error`) でエラーが編集中を
   * 差し替える。 地色は変えない (isEditing の背景がそのまま残る想定)。
   * locked は編集不可なので編集中バーは出さない (`enabled && !locked` の薄枠と
   * 同じ理由)。 エラーは locked を見ない (design も見ていない)。
   */
  const showsEditingBar = isEditing && !locked && !hasSaveError
  const showsErrorMark = hasSaveError
  // **`locked` で除外しない** (レビュー指摘 #7216)。理由は cellStyle の outline のコメント。
  const showsOverContractOutline = isOverContract
  const editingOrErrorBoxShadow = showsErrorMark
    ? `inset 3px 0 0 0 ${LightModeSemanticColors.Text.negativeHigh.value}`
    : showsEditingBar
      ? `inset 3px 0 0 0 ${LightModeSemanticColors.Surface.accentPrimary.value}`
      : undefined
  /*
   * 値 (input の文字) の強調。 GC ロック済セルはロック外観 (低強調) を保ち、
   * エラー (保存拒否) は超過警告より優先する (下の input の color コメント参照)。
   */
  const valueEmphasis: 'save-error' | 'over-contract' | undefined = locked
    ? undefined
    : hasSaveError
      ? 'save-error'
      : isOverContract
        ? 'over-contract'
        : undefined
  /*
   * 選択の枠 (design .s48-range-sel: inset 0 0 0 2px border-accent-primary)。
   * 複数セル範囲 (isSelected) と単一セル focus (isFocused) の両方を表す。
   */
  const selectionBoxShadow = showSelectionRing
    ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
    : undefined
  /*
   * [レビュー指摘 #9031] 編集中/エラーの左バーと選択・focus 枠は**同じセルに
   * 同時に成立しうる**別々の視覚要素で、 design もそう扱う (`48data.html:213-217`
   * の cell-edited focus ルール「一度編集したセルではアクティブセルの枠が
   * 一切見えなくなっていた」の修正コメント、 `:254-256` の cell-save-error focus
   * ルール「focus した時も赤バーを残す」)。 box-shadow は 1 セル 1 プロパティしか
   * 持てないため、 2 つの inset 値をカンマ区切りで 1 つの box-shadow に合成する
   * (どちらか一方だけを個別に spread で上書きすると、 選択中は編集中/エラーの
   * バーが消えていた)。
   */
  const stateBoxShadow = [editingOrErrorBoxShadow, selectionBoxShadow]
    .filter((shadow): shadow is string => shadow !== undefined)
    .join(', ')

  const cellStyle: React.CSSProperties = {
    padding: 0,
    textAlign: 'right',
    borderBottom: isKomaDividerRow
      ? KOMA_DIVIDER_BORDER
      : `1px solid ${COLOR_BORDER_LOW}`,
    borderRight: `1px solid ${COLOR_BORDER_LOW}`,
    position: 'relative',
    background: bgColor,
    // #4343 (design/supply/48data.html:138-139): sub 列ヘッダ th と同じ明示幅。
    // 内側 input の minWidth:0 (下記) と対で、input の既定 intrinsic 幅
    // (≒177px) に auto layout が引きずられて列が間延びするのを防ぐ。
    width: S48_DATA_COL_WIDTH_PX,
    ...(showsOverContractOutline
      ? {
          // design `supply/48data.html:328-334` の `td.over-contract`:
          // `outline: 2px solid var(--text-warning-high)` (#5d4e00)。
          // 旧実装の `#f59e0b` は Tailwind amber-500 で **DS のどの token でもない**
          // (コメントの「eclair: border-warning」 は誤記)。 amber と DS の warning は
          // 色相ごと別物で、 同じ画面の cell-empty (negative) や混雑処理 (info) との
          // 識別設計が崩れる。
          //
          // **`locked` で除外しない** (レビュー指摘 #7216)。 8080 の同じ位置に
          // 「超過は続行可能 (スルー可) のため warning(黄)。 **outline は常に表示して
          // 違反をフラグ**。 背景は非ロックセルで黄、 GCロック済セルは gc-locked が勝ち
          // グレー(ロック外観)を維持 — **ロック状態を保ちつつ outline で違反を明示する
          // 意図的挙動**」 と明記されている (48data.html:322-324)。 CSS 上も
          // `.gc-locked` は outline を持たないので `.over-contract` の outline が残る。
          // 除外していると、 GC 後に契約値を超えたセルが灰色一色になり超過を識別できない。
          outline: `2px solid ${LightModeSemanticColors.Text.warningHigh.value}`,
          outlineOffset: '-2px',
        }
      : {}),
    ...(showsErrorMark
      ? {
          outline: `2px solid ${LightModeSemanticColors.Text.negativeHigh.value}`,
          outlineOffset: '-2px',
        }
      : {}),
    // 薄枠 (enabled && !locked) は編集中/エラーのバーにも選択枠にも道を譲る
    // 素の編集可能セル専用の表現なので、 stateBoxShadow が空の時だけ出す
    ...(stateBoxShadow === '' && enabled && !locked
      ? {
          boxShadow: 'inset 0 0 0 1px rgba(59, 130, 246, 0.2)',
        }
      : {}),
    ...(stateBoxShadow !== '' ? { boxShadow: stateBoxShadow } : {}),
    // AC-S4D-052: 遷移直後の一時列ハイライト。design `.s48-chart-col`
    // (48data.html:418: 列の左右を 2px の accent 枠で囲む) 準拠。選択枠とは
    // 意図的に別の視覚語彙にする (Des 判断、PR #7935 レビュー 2026-09-15)。
    // 選択枠と独立の条件なので、選択中に一時ハイライトが重なっても両方描かれる。
    ...(isScrollHighlighted
      ? {
          borderLeft: `2px solid ${LightModeSemanticColors.Border.accentPrimary.value}`,
          borderRight: `2px solid ${LightModeSemanticColors.Border.accentPrimary.value}`,
        }
      : {}),
  }

  return (
    // aria-selected は暗黙 role `cell` の td には許可されない属性 (axe
    // aria-allowed-attr 違反) のため、選択状態の観測 seam は data 属性で持つ。
    // 他の状態 (編集中バー・超過・値の強調・一時列ハイライト) も同じく data 属性で出す。
    <td
      data-editing={showsEditingBar || undefined}
      data-over-contract={showsOverContractOutline || undefined}
      data-save-error={showsErrorMark || undefined}
      data-saved-manual={isSavedManual || undefined}
      data-scroll-highlighted={isScrollHighlighted || undefined}
      data-selected={showSelectionRing || undefined}
      data-value-emphasis={valueEmphasis}
      style={cellStyle}
    >
      {/*
       * [#5125 / #8988 / AC-S4D-049] 変更後 (保存済みの手動変更): セル下辺の
       * 横 2px 下線 (design `48data.html:267-273` の `cell-manual::after`)。
       * 地色ではなく専用マーカーにする理由は gridStyles.ts の isEditing コメント
       * 参照 (6 系統目の地色を足すと「色が付いている = 何かある」以上が読めなく
       * なる)。 GC ロック済セルは中立色にして「操作できない」外観と揃える。
       */}
      {isSavedManual && (
        <span
          aria-hidden="true"
          data-testid="saved-manual-marker"
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            bottom: 2,
            height: 2,
            // design `48data.html:273` の `.cell-manual.gc-locked::after` は
            // `var(--border-mid)` (Border.midEmphasis)。 COLOR_BORDER_LOW
            // (Border.lowEmphasis、セル罫線に使用中) とは別 token。
            background: locked
              ? LightModeSemanticColors.Border.midEmphasis.value
              : LightModeSemanticColors.Surface.accentPrimary.value,
            pointerEvents: 'none',
          }}
        />
      )}
      <input
        aria-label={`${dateLabel} ${komaLabel} ${columnLabel}`}
        data-col={col}
        data-row={row}
        // issue #5120: ロックセル (GC 済み) は「編集できないが選択・コピーは
        // できる」Excel の保護セル相当にする。disabled にすると focus / 選択
        // 自体ができずネイティブコピーにも乗れないため、書き込み不可は
        // readOnly (下記) だけで表現し、disabled は「閲覧モードでこのセル
        // 自体が対象外」の場合 (!enabled) に限定する。
        disabled={!enabled}
        inputMode="numeric"
        onBlur={() => {
          setIsFocused(false)
        }}
        onChange={onChange}
        onFocus={() => {
          setIsFocused(true)
        }}
        // P5: disabled (!enabled) が既にセル全体を非対話にするため、readOnly
        // 側で !enabled を重ねるのは冗長 (disabled 時は readOnly の値を
        // ブラウザが見ない)。ここでは locked だけを見れば足りる。
        readOnly={locked}
        style={{
          width: '100%',
          // #4343: input の既定 intrinsic 幅 (size 未指定で ≒177px) が
          // auto layout の列幅算出に紛れ込むのを防ぐ (design 48data.html:141 と同じ)
          minWidth: 0,
          padding: '6px',
          textAlign: 'right',
          border: 'none',
          background: 'transparent',
          /* design `td.over-contract` は地色・枠だけでなく **文字色 (warning-high) と
             font-weight 600** も持つ (48data.html:332-333)。 実装はそこが抜けており、
             超過セルの値が通常セルと同じ黒・通常太さで出ていた。
             `locked` を先に見るのは、 GC ロック済セルはロック外観 (低強調) を保つ
             8080 の意図的挙動に合わせるため (48data.html:322-325 のコメント)。
             [#5125 / #8988 / AC-S4D-049] エラー (negative-high・weight 600、
             design `48data.html:253` の `cell-save-error input`) は超過警告より
             優先する (保存を確実にブロックする方がスルー可能な警告より緊急)。
             編集中・変更後 (isEditing / isSavedManual) は design 上、文字色・
             太さを変えない (バー・下線マーカーのみで表す)。 */
          color: locked
            ? COLOR_TEXT_LOW
            : valueEmphasis === 'save-error'
              ? LightModeSemanticColors.Text.negativeHigh.value
              : valueEmphasis === 'over-contract'
                ? LightModeSemanticColors.Text.warningHigh.value
                : COLOR_TEXT_HIGH,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: valueEmphasis !== undefined ? 600 : 400,
          outline: 'none',
          cursor: locked ? 'not-allowed' : enabled ? 'cell' : 'default',
        }}
        // [#5125 / #8988 / AC-S4D-049] エラー理由を native title で持つ。
        // design `48data.html:225` の `data-tooltip` と同じ理由 (器が overflow を
        // 持つため DS Tooltip コンポーネントではなく native tooltip を使う)。
        // バナー (index.tsx の rejectionReport) と役割は同じだが、こちらは
        // 「どのセルが拒否されたか」をセル自体に固定する。
        title={saveErrorTooltip}
        value={formatCellValue(displayValue)}
      />
    </td>
  )
})
